/**
 * ProgressShareTab — controls to generate/download a share card.
 *
 * Kept as an isolated component so the ProgressPage tabs stay slim.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Trash2, Copy, Check } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import ShareCard from "./ShareCard";

function publicUrlFor(share_id) {
    if (typeof window === "undefined") return `/api/og/partage/${share_id}`;
    // Surface the backend OG page as the shareable URL: crawlers get rich
    // preview metadata, humans are redirected to the SPA /partage/{id}.
    return `${window.location.origin}/api/og/partage/${share_id}`;
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("fr-FR", {
            day: "numeric",
            month: "short",
            year: "numeric",
        });
    } catch {
        return "—";
    }
}

export default function ShareTab() {
    const [snapshot, setSnapshot] = useState(null);
    const [shareId, setShareId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(true);
    const [shares, setShares] = useState([]);
    const [copiedId, setCopiedId] = useState(null);
    const cardRef = useRef(null);

    async function refreshShares() {
        try {
            const { data } = await api.get("/share/mine");
            setShares(data.shares || []);
        } catch {
            /* silent */
        }
    }

    useEffect(() => {
        (async () => {
            try {
                const [{ data }, { data: prof }] = await Promise.all([
                    api.get("/dashboard/progress"),
                    api.get("/profile/me"),
                ]);
                setSnapshot({
                    first_name: prof.profile?.first_name || "Apprenant",
                    target_language_fr: prof.profile?.target_language || "—",
                    variant: prof.profile?.variant || null,
                    level: prof.profile?.level || "A1",
                    words_mastered: data.stats?.words_mastered || 0,
                    words_active: data.stats?.words_active || 0,
                    words_discovered: data.stats?.words_discovered || 0,
                    streak_days: data.stats?.streak_days || 0,
                });
                await refreshShares();
            } catch (err) {
                toast.error(formatApiError(err));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    async function createShare() {
        setCreating(true);
        try {
            const { data } = await api.post("/share/card");
            setShareId(data.share_id);
            setSnapshot(data.snapshot);
            toast.success("Lien de partage créé.");
            await refreshShares();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setCreating(false);
        }
    }

    async function revokeShare(id) {
        if (!window.confirm("Révoquer ce lien ? Il deviendra inaccessible aux personnes qui l'ont enregistré.")) return;
        try {
            await api.post(`/share/${id}/revoke`);
            toast.success("Lien révoqué.");
            await refreshShares();
            if (shareId === id) setShareId(null);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    }

    async function copyLink(id) {
        try {
            await navigator.clipboard.writeText(publicUrlFor(id));
            setCopiedId(id);
            toast.success("Lien copié.");
            setTimeout(() => setCopiedId(null), 2000);
        } catch {
            toast.error("Impossible de copier le lien.");
        }
    }

    const activeShares = useMemo(() => (shares || []).filter((s) => !s.revoked), [shares]);
    const publicUrl = shareId ? publicUrlFor(shareId) : null;

    if (loading || !snapshot) {
        return (
            <div className="flex items-center justify-center py-12" data-testid="share-loading">
                <Loader2 className="w-5 h-5 animate-spin text-terracotta" />
            </div>
        );
    }

    return (
        <div data-testid="share-tab">
            <div className="bg-white border border-lexiBorder rounded-2xl p-4 mb-6 flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald/12 flex items-center justify-center shrink-0">
                    <Sparkles className="w-4 h-4 text-emerald" strokeWidth={1.75} />
                </div>
                <div className="flex-1">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft">
                        Carte de progression partageable
                    </p>
                    <p className="font-serif text-lg mt-0.5">
                        Une image élégante de votre parcours.
                    </p>
                    <p className="text-[11px] text-inkSoft mt-1 max-w-md">
                        Téléchargez-la ou générez un lien public en lecture seule.
                        Aucune donnée sensible n&apos;est publiée.
                    </p>
                </div>
                {!shareId && (
                    <button
                        onClick={createShare}
                        disabled={creating}
                        data-testid="share-create-link"
                        className="inline-flex items-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-4 py-2 text-sm shrink-0 disabled:opacity-60 transition-colors"
                    >
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {activeShares.length > 0 ? "Créer un nouveau lien" : "Créer un lien public"}
                    </button>
                )}
                {shareId && (
                    <button
                        onClick={() => setShareId(null)}
                        data-testid="share-create-another"
                        className="inline-flex items-center gap-2 bg-paperAlt border border-lexiBorder hover:bg-paper rounded-full px-4 py-2 text-sm shrink-0 transition-colors"
                    >
                        Créer un autre lien
                    </button>
                )}
            </div>

            <ShareCard ref={cardRef} snapshot={snapshot} publicUrl={publicUrl} />

            {publicUrl && (
                <p className="mt-3 text-[11px] text-inkSoft italic" data-testid="share-public-url">
                    Lien public : <span className="font-mono text-ink">{publicUrl}</span>
                </p>
            )}

            {activeShares.length > 0 && (
                <section className="mt-8" data-testid="share-active-list">
                    <p className="text-[11px] tracking-[0.22em] uppercase text-inkSoft mb-3">
                        Vos liens de partage actifs
                    </p>
                    <ul className="space-y-2">
                        {activeShares.map((s) => (
                            <li
                                key={s.share_id}
                                className="bg-white border border-lexiBorder rounded-xl p-3 flex items-center gap-3"
                                data-testid={`share-row-${s.share_id}`}
                            >
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm font-mono text-ink truncate">
                                        /partage/{s.share_id}
                                    </p>
                                    <p className="text-[10px] tracking-[0.22em] uppercase text-inkSoft mt-0.5">
                                        Créé le {formatDate(s.created_at)}
                                    </p>
                                </div>
                                <button
                                    onClick={() => copyLink(s.share_id)}
                                    data-testid={`share-copy-${s.share_id}`}
                                    className="inline-flex items-center gap-1.5 bg-paperAlt border border-lexiBorder hover:bg-paper rounded-full px-3 py-1.5 text-xs transition-colors"
                                >
                                    {copiedId === s.share_id ? (
                                        <Check className="w-3.5 h-3.5 text-emerald" />
                                    ) : (
                                        <Copy className="w-3.5 h-3.5" />
                                    )}
                                    Copier
                                </button>
                                <button
                                    onClick={() => revokeShare(s.share_id)}
                                    data-testid={`share-revoke-${s.share_id}`}
                                    className="inline-flex items-center gap-1.5 bg-terracotta/10 text-terracotta hover:bg-terracotta hover:text-white border border-terracotta/30 rounded-full px-3 py-1.5 text-xs transition-colors"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                    Révoquer
                                </button>
                            </li>
                        ))}
                    </ul>
                </section>
            )}
        </div>
    );
}
